import React, { useState } from 'react';
import { Icons } from '../ui/Icons';
import { StaffMember, Shift, AssignmentStatus, Schedule, ScheduleEntry, DayOfWeek, AdminUser } from '../../types';
import { formatDateYYYYMMDD } from '../../utils/dateUtils';
import { toast } from 'react-hot-toast';

type SchedulablePerson = (StaffMember & { uid?: never }) | (Omit<AdminUser, 'uid'> & { id: string });

interface ImportScheduleModalProps {
    people: SchedulablePerson[];
    shifts: Shift[];
    onClose: () => void;
    onImport: (newSchedule: Schedule, weekStartDate: Date) => void;
}

const FRENCH_DAY_MAP: Record<string, string> = {
    'LUN': 'Monday',
    'MAR': 'Tuesday',
    'MER': 'Wednesday',
    'JEU': 'Thursday',
    'VEN': 'Friday',
    'SAM': 'Saturday',
    'DIM': 'Sunday'
};

const FRENCH_DEPT_MAP: Record<string, string> = {
    'RECEPTION': 'Front Desk',
    'AUDITEUR DE NUIT': 'Night Auditor',
    'AGENTS DE SECURITE': 'Security Agents',
    'CHAUFFEURS': 'Drivers'
};

const MONTH_MAP: Record<string, string> = {
    'JAN': 'Jan', 'JANV': 'Jan',
    'FEV': 'Feb', 'FEVR': 'Feb',
    'MAR': 'Mar', 'MARS': 'Mar',
    'AVR': 'Apr', 'AVRI': 'Apr',
    'MAI': 'May',
    'JUIN': 'Jun',
    'JUIL': 'Jul',
    'AOUT': 'Aug',
    'SEPT': 'Sep',
    'OCT': 'Oct',
    'NOV': 'Nov',
    'DEC': 'Dec'
};

const ImportScheduleModal: React.FC<ImportScheduleModalProps> = ({ people, shifts, onClose, onImport }) => {
    const [pasteArea, setPasteArea] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const parseShifts = (text: string) => {
        console.log("Starting Import Normalization with high accuracy parser...");

        // Split text into rows and columns, preserving empty cells where tabs exist
        const rawRows = text.split('\n').filter(r => r.trim());
        const rows = rawRows.map(originalRow => {
            if (originalRow.includes('\t')) {
                return originalRow.split('\t').map(cell => cell.trim());
            }
            // Fallback for space-separated grids (try to detect columns)
            return originalRow.split(/\s{2,}/).map(cell => cell.trim());
        });
        
        let dateRowIndex = -1;

        // 1. Identify the anchor row (DATE)
        for (let i = 0; i < rows.length; i++) {
            const rowUpper = rows[i].map(c => c.toUpperCase());
            // Look for a row that clearly contains dates like "4-May"
            if (rowUpper.some(cell => /\d+-[A-Z]+/i.test(cell))) {
                dateRowIndex = i;
                break;
            }
        }

        if (dateRowIndex === -1) {
            toast.error('Could not find the DATE row (e.g. 4-May, 5-May...) in the data.');
            return;
        }

        const dateRow = rows[dateRowIndex];
        // 2. Identify where Monday starts
        const dateStartIndex = dateRow.findIndex(c => /\d+-[A-Z]+/i.test(c));
        
        if (dateStartIndex === -1) {
            toast.error('Could not identify date columns.');
            return;
        }

        const dateCells = dateRow.slice(dateStartIndex, dateStartIndex + 7);
        const parsedDates: Date[] = [];
        const currentYear = new Date().getFullYear();

        for (const cell of dateCells) {
            if (!cell) continue;
            const match = cell.match(/(\d+)-([A-Za-z]+)/);
            if (match) {
                const day = parseInt(match[1]);
                let monthName = match[2].toUpperCase();
                Object.keys(MONTH_MAP).forEach(key => {
                    if (monthName.startsWith(key)) {
                        monthName = MONTH_MAP[key].toUpperCase();
                    }
                });

                let date = new Date(`${day} ${monthName} ${currentYear}`);
                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                if (date < sixMonthsAgo) {
                    date = new Date(`${day} ${monthName} ${currentYear + 1}`);
                }
                
                if (!isNaN(date.getTime())) {
                    parsedDates.push(date);
                }
            }
        }

        if (parsedDates.length < 7) {
            toast.error(`Found ${parsedDates.length}/7 dates. Please ensure 7 consecutive dates are present.`);
            return;
        }

        const weekStartDate = parsedDates[0];
        const dateStrings = parsedDates.map(d => formatDateYYYYMMDD(d));

        const newSchedule: Schedule = {};
        const ignoredRows: string[] = [];

        // Helper: Parse "8:00 AM - 4:00 PM" into {start, end}
        const parseShiftTime = (str: string) => {
            // Regex supports: "8:00 AM - 4:00 PM", "9 A - 5 P", "11 PM - 7 AM"
            const timeMatch = str.match(/(\d+):?(\d+)?\s*(AM|PM|A|P)?\s*-\s*(\d+):?(\d+)?\s*(AM|PM|A|P)?/i);
            if (!timeMatch) return null;

            const convertTo24 = (h: string, m: string|undefined, p: string|undefined) => {
                let hour = parseInt(h);
                const min = m ? m.padStart(2, '0') : '00';
                let period = p ? p.toUpperCase() : '';
                if (period === 'A') period = 'AM';
                if (period === 'P') period = 'PM';
                
                if (period === 'PM' && hour < 12) hour += 12;
                if (period === 'AM' && hour === 12) hour = 0;
                
                return `${hour.toString().padStart(2, '0')}:${min}`;
            };

            return {
                start: convertTo24(timeMatch[1], timeMatch[2], timeMatch[3]),
                end: convertTo24(timeMatch[4], timeMatch[5], timeMatch[6])
            };
        };

        // 3. Process staff rows
        for (let i = 0; i < rows.length; i++) {
            // Skip the header rows
            if (i <= dateRowIndex) continue;

            const row = rows[i];
            if (row.length < 2) continue;

            const name = row[0].trim();
            const upperName = name.toUpperCase();
            
            // Skip common non-staff headers
            if (!name || 
                upperName === 'JOUR' || upperName === 'DAY' || 
                upperName === 'DATE' || upperName === 'TOTAL' ||
                upperName.includes('LES HORAIRES SONT SUJETS') ||
                upperName.includes('THE SCHEDULES ARE SUBJECT') ||
                Object.keys(FRENCH_DEPT_MAP).some(dept => upperName.includes(dept)) ||
                (name.includes('/') && row.slice(1).every(c => !c))
            ) {
                continue;
            }

            // Fuzzy match staff name
            const staff = people.find(p => p.name.trim().toLowerCase() === name.toLowerCase());
            if (!staff) {
                ignoredRows.push(name);
                continue;
            }

            if (!newSchedule[staff.id]) {
                newSchedule[staff.id] = {};
            }

            // 4. Map daily shifts using dateStartIndex as reference
            for (let j = 0; j < 7; j++) {
                const cellIndex = dateStartIndex + j;
                // Some rows might be shorter if trailing cells are empty
                if (cellIndex >= row.length) continue;
                
                const shiftStr = row[cellIndex];
                if (!shiftStr) continue;

                const dateStr = dateStrings[j];
                const upperShift = shiftStr.toUpperCase().trim();
                
                let status: AssignmentStatus = 'ASSIGNED';
                let shiftId: string | null = null;
                let customStartTime: string | undefined;
                let customEndTime: string | undefined;

                if (upperShift === 'R' || upperShift === 'REPOS' || upperShift === 'OFF' || upperShift === 'REST DAY') {
                    status = 'OFF';
                } else if (upperShift.includes('SUR APPEL') || upperShift === 'APP' || upperShift === 'ON CALL') {
                    status = 'ON_CALL';
                } else if (upperShift === 'SICK' || upperShift === 'MALADE') {
                    status = 'SICK';
                } else if (upperShift === 'VACATION' || upperShift === 'VACANCES' || upperShift === 'VAC') {
                    status = 'VACATION';
                } else if (upperShift === 'REFUSED' || upperShift === 'REFUS' || upperShift === 'REF') {
                    status = 'REFUSED';
                } else {
                    // Split shift handling (takes first shift found)
                    const firstPart = shiftStr.split('/')[0].trim();
                    const parsedTime = parseShiftTime(firstPart);

                    if (parsedTime) {
                        const matchedShift = shifts.find(s => s.startTime === parsedTime.start && s.endTime === parsedTime.end);
                        if (matchedShift) {
                            shiftId = matchedShift.id;
                        } else {
                            customStartTime = parsedTime.start;
                            customEndTime = parsedTime.end;
                        }
                    } else {
                        // Fallback to shift name match
                        const namedShift = shifts.find(s => s.name.toUpperCase() === upperShift);
                        if (namedShift) {
                            shiftId = namedShift.id;
                        } else {
                             status = 'OFF';
                        }
                    }
                }

                newSchedule[staff.id][dateStr] = {
                    staffId: staff.id,
                    shiftId,
                    status,
                    customStartTime,
                    customEndTime,
                    isTimeEdited: !!(customStartTime || customEndTime)
                };
            }
        }

        if (ignoredRows.length > 0) {
            console.warn("Staff not found for rows:", ignoredRows);
        }

        const processedCount = Object.keys(newSchedule).length;
        if (processedCount > 0) {
            onImport(newSchedule, weekStartDate);
            toast.success(`Schedule imported accurately for ${processedCount} staff members.`);
            if (ignoredRows.length > 0) {
                toast(`Note: ${ignoredRows.length} rows were skipped (staff names not found).`, { icon: 'ℹ️' });
            }
        } else {
            toast.error('No matching staff members found in the data.');
        }
    };

    const handleImport = () => {
        if (!pasteArea.trim()) {
            toast.error('Please paste the schedule data first.');
            return;
        }
        setIsProcessing(true);
        setTimeout(() => {
            try {
                parseShifts(pasteArea);
            } catch (error) {
                console.error('Import error:', error);
                toast.error('Failed to parse schedule. Please check the format.');
            } finally {
                setIsProcessing(false);
            }
        }, 500);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-slate-700">
                <div className="px-6 py-4 bg-indigo-600 dark:bg-indigo-700 flex justify-between items-center text-white">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                            <Icons.Upload className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold">Import Weekly Schedule</h3>
                            <p className="text-xs text-indigo-100/80">Copy-paste from Excel or PDF table</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full transition-colors">
                        <Icons.X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="mb-6">
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                            Paste Grid Data Here
                        </label>
                        <div className="relative group">
                            <textarea
                                value={pasteArea}
                                onChange={(e) => setPasteArea(e.target.value)}
                                placeholder="Paste your schedule table here (select all in Excel and copy)...&#10;Expected format: Columns for Name, Phone, and 7 Days."
                                className="w-full h-80 px-4 py-3 bg-gray-50 dark:bg-slate-900/50 border-2 border-gray-100 dark:border-slate-700 rounded-xl font-mono text-sm focus:border-indigo-500 focus:ring-0 outline-none transition-all dark:text-white dark:placeholder-gray-500"
                            />
                            {pasteArea && (
                                <button 
                                    onClick={() => setPasteArea('')}
                                    className="absolute top-3 right-3 p-1.5 bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Clear all"
                                >
                                    <Icons.RefreshCw className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="mt-4 flex items-center gap-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl">
                            <Icons.Info className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                            <p className="text-xs text-amber-800 dark:text-amber-200">
                                <strong>Tip:</strong> Ensure you include the row containing 'DATE' with '4-May' etc. format headers. The importer matches staff members by their full name.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={onClose}
                            className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 font-bold hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleImport}
                            disabled={isProcessing || !pasteArea.trim()}
                            className="flex items-center gap-2 px-8 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold shadow-lg shadow-indigo-500/20 transition-all"
                        >
                            {isProcessing ? (
                                <>
                                    <Icons.Loader2 className="w-5 h-5 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Icons.Check className="w-5 h-5" />
                                    Import Schedule
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportScheduleModal;
